import Prompt, { PromptRating, PromptStatus } from "@/models/prompt/prompt";
import { PATH_KEYS } from "../services/appServices";
import { useItemsCrud } from "./useItemsCrud";
import { useItemService } from "./useServices";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pagination, PaginationQuery } from "@/models/base/Pagination";
import { restError } from "@/models/client/RestResponse";

// Jeg har fjernet al invalidation af queries, ved mutationer fordi det ødelægger vorer LazyLoad infinite scroll pagination.
// Vi håndtere det i stedet ved at mutere dataen direkte, og lade infinite scroll håndtere at hente mere data. (Se PromptLibraryListView.tsx)
const usePrompts = () => {
  /**** Services ****/
  const promptService = useItemService((services) => services.prompt);
  const queryClient = useQueryClient();

  const promptCrud = useItemsCrud<Prompt>(
    [PATH_KEYS.prompt],
    (services) => services.prompt
  );

  /**** Queries & Mutations ****/
  const useQueryMyPrompts = (query: PaginationQuery, signal?: AbortSignal) =>
    useQuery<Pagination<Prompt>>({
      queryKey: [
        PATH_KEYS.prompt,
        "myPrompts",
        query.pageNumber,
        query.pageSize,
        query.search,
        query.sortBy,
        query.sortAscending,
        query.filters,
      ],
      queryFn: async () => {
        const response = await promptService.getMyPrompts(query, signal);
        if (response.success) return response.value;
        else throw restError(response);
      },
      staleTime: 0,
      refetchInterval: 0,
    });

  const useQueryPrompts = (params: PaginationQuery, signal?: AbortSignal) =>
    useQuery<Pagination<Prompt>>({
      queryKey: [
        PATH_KEYS.prompt,
        "pagination",
        params.pageNumber,
        params.pageSize,
        params.search,
        params.sortBy,
        params.sortAscending,
        params.filters,
      ],
      queryFn: async () => {
        const response = await promptService.queryPagination(params, signal);
        if (response.success) return response.value;
        else throw restError(response);
      },
      staleTime: 0,
    });

  const updateStatus = useMutation<
    string,
    string,
    { id: string; status: PromptStatus }
  >({
    mutationKey: [PATH_KEYS.prompt, "updateStatus"],
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: PromptStatus;
    }) => {
      const response = await promptService.updatePromptStatus(id, status);
      if (response.success) return response.value;
      else throw restError(response);
    },
  });

  const updateStatusAndInvalidate = useMutation<
    string,
    string,
    { id: string; status: PromptStatus }
  >({
    mutationKey: [PATH_KEYS.prompt, "updateStatus"],
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: PromptStatus;
    }) => {
      const response = await promptService.updatePromptStatus(id, status);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.prompt] });
    },
  });

  const usePrompt = useMutation<string, string, string>({
    mutationKey: [PATH_KEYS.prompt, "use"],
    mutationFn: async (id: string) => {
      const response = await promptService.usePrompt(id);
      if (response.success) return response.value;
      else throw restError(response);
    },
    // onSuccess: () => {
    //   queryClient.invalidateQueries({ queryKey: [PATH_KEYS.prompt] });
    // },
  });

  const ratePrompt = useMutation<
    string,
    string,
    { id: string; rating: PromptRating }
  >({
    mutationKey: [PATH_KEYS.prompt, "rate"],
    mutationFn: async ({
      id,
      rating,
    }: {
      id: string;
      rating: PromptRating;
    }) => {
      const response = await promptService.ratePrompt(id, rating);
      if (response.success) return response.value;
      else throw restError(response);
    },
    // onSuccess: () => {
    //   queryClient.invalidateQueries({ queryKey: [PATH_KEYS.prompt] });
    // },
  });

  const favoritePrompt = useMutation<string, string, string>({
    mutationKey: [PATH_KEYS.prompt, "favorite"],
    mutationFn: async (id: string) => {
      const response = await promptService.favoritePrompt(id);
      if (response.success) return response.value;
      else throw restError(response);
    },
    // onSuccess: () => {
    //   queryClient.invalidateQueries({ queryKey: [PATH_KEYS.prompt] });
    // },
  });

  const deletePrompt = useMutation<string, string, string>({
    mutationKey: [PATH_KEYS.prompt, "delete"],
    mutationFn: async (id: string) => {
      const response = await promptService.deletePrompt(id);
      if (response.success) return response.value;
      else throw restError(response);
    },
    // onSuccess: () => {
    //   queryClient.invalidateQueries({ queryKey: [PATH_KEYS.prompt] });
    // },
  });

  /**** Value ****/
  return {
    ...promptCrud,
    useQueryMyPrompts,
    useQueryPrompts,
    updateStatus,
    usePrompt,
    ratePrompt,
    favoritePrompt,
    deletePrompt,
    updateStatusAndInvalidate,
  };
};

export default usePrompts;
